/* eslint-disable unicode-bom */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

declare global {
  interface Window {
    WOW: any;
  }
}

interface DonationFormData {
  donationType: string;
  amount: string;
  customAmount: string;
  fullName: string;
  email: string;
  contactNumber: string;
  country: string;
  message: string;
  agreedToTerms: boolean;
}

const Donate: React.FC = () => {
  const [formData, setFormData] = useState<DonationFormData>({
    donationType: 'one-time',
    amount: '',
    customAmount: '', 
    fullName: '',
    email: '',
    contactNumber: '',
    country: '',
    message: '',
    agreedToTerms: false
  });

  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');

  // Custom checkbox/radio styles
  const checkboxStyles = useMemo(() => `
  .custom-checkbox {
    position: relative;
    display: inline-block;
    cursor: pointer;
    user-select: none;
  }

  .custom-checkbox input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }

  .custom-checkbox .checkbox-box {
    position: relative;
    display: inline-block;
    width: 22px;
    height: 22px;
    background-color: white;
    border: 2px solid #ddd;
    border-radius: 5px;
    transition: all 0.3s ease;
  }

  .custom-checkbox:hover .checkbox-box {
    border-color: #03A84A;
  }

  .custom-checkbox input[type="checkbox"]:checked ~ .checkbox-box {
    background-color: #03A84A;
    border-color: #03A84A;
  }

  .custom-checkbox .checkbox-box::after {
    content: "";
    position: absolute;
    display: none;
    left: 6px;
    top: 2px;
    width: 6px;
    height: 11px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  .custom-checkbox input[type="checkbox"]:checked ~ .checkbox-box::after {
    display: block;
    animation: checkmark 0.2s ease-in-out;
  }

  @keyframes checkmark {
    0% { transform: rotate(45deg) scale(0); }
    100% { transform: rotate(45deg) scale(1); }
  }

  .custom-radio {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
  }

  .custom-radio input[type="radio"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
  }

  .custom-radio .radio-box {
    position: relative;
    display: inline-block;
    width: 20px;
    height: 20px;
    background-color: white;
    border: 2px solid #ddd;
    border-radius: 50%;
    transition: all 0.3s ease;
    margin-right: 8px;
  }

  .custom-radio:hover .radio-box {
    border-color: #03A84A;
  }

  .custom-radio input[type="radio"]:checked ~ .radio-box {
    border-color: #03A84A;
  }

  .custom-radio .radio-box::after {
    content: "";
    position: absolute;
    display: none;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #03A84A;
  }

  .custom-radio input[type="radio"]:checked ~ .radio-box::after {
    display: block;
    animation: radiomark 0.2s ease-in-out;
  }

  @keyframes radiomark {
    0% { transform: translate(-50%, -50%) scale(0); }
    100% { transform: translate(-50%, -50%) scale(1); }
  }
  `, []);

  useEffect(() => {
    // Initialize data-background attributes
    const elements = document.querySelectorAll('[data-background]');
    elements.forEach((element) => {
      const bg = element.getAttribute('data-background');
      if (bg) {
        (element as HTMLElement).style.backgroundImage = `url(${bg})`;
      }
    });

    // Initialize WOW.js for scroll animations
    if (window.WOW) {
      const wow = new window.WOW({
        boxClass: 'wow',
        animateClass: 'animated',
        offset: 0,
        mobile: true,
        live: true
      });
      wow.init();
    }

    // Inject custom styles
    const styleElement = document.createElement('style');
    styleElement.innerHTML = checkboxStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, [checkboxStyles]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRadioChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = () => {
    setFormData(prev => ({ ...prev, agreedToTerms: !prev.agreedToTerms }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submission started');
    console.log('Form data:', formData);
    
    if (!formData.agreedToTerms) {
      console.log('Error: Terms not agreed');
      setSubmitError('Please agree to the terms and conditions before proceeding');
      setTimeout(() => setSubmitError(''), 3000);
      return;
    }

    if (!formData.amount && !formData.customAmount) {
      console.log('Error: No amount selected');
      setSubmitError('Please select or enter a donation amount');
      setTimeout(() => setSubmitError(''), 3000);
      return;
    }

    try {
      const finalAmount = formData.amount === 'custom' ? formData.customAmount : formData.amount;
      // Submit donation request to backend (team follows up by email/phone)
      const donationData = {
        donationType: formData.donationType,
        amount: finalAmount,
        fullName: formData.fullName,
        email: formData.email,
        contactNumber: formData.contactNumber,
        country: formData.country,
        message: formData.message,
        agreedToTerms: formData.agreedToTerms
      };
      const response = await fetch('http://localhost:5000/api/donations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(donationData)
      });
      await response.json();
      if (response.ok) {
        setSubmitMessage('Thank you for your generous donation! We will process your donation and contact you soon.');
        setSubmitError('');
        setFormData({
          donationType: 'one-time',
          amount: '',
          customAmount: '',
          fullName: '',
          email: '',
          contactNumber: '',
          country: '',
          message: '',
          agreedToTerms: false
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => setSubmitMessage(''), 5000);
      } else {
        setSubmitError('Failed to submit donation. Please try again.');
        setTimeout(() => setSubmitError(''), 3000);
      }
    } catch (error) {
      setSubmitError('An error occurred. Please try again later.');
      setTimeout(() => setSubmitError(''), 3000);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 15px',
    border: '2px solid #e8e8e8',
    borderRadius: '8px',
    fontSize: '15px',
    transition: 'border-color 0.3s ease',
    fontFamily: 'inherit'
  };

  const predefinedAmounts = ['10', '25', '50', '100', '250', '500'];

  return (
    <>
      {/* Page Banner */}
      <section 
        className="banner__inner-page bg-image pt-160 pb-160" 
        data-background="/assets/images/banner/banner-inner-page.jpg"
      >
        <div className="container">
          <h2 className="wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms">Make a Donation</h2>
          <div className="breadcrumb-list wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms">
            <Link to="/">Home</Link>
            <span><i className="fa-regular fa-angles-right mx-2"></i>Donate</span>
          </div>
        </div>
      </section>

      {/* Donation Section */}
      <section style={{ padding: '80px 0', backgroundColor: '#f9fafb' }}>
        <div className="container">
          {submitMessage && (
            <div style={{
              padding: '15px 20px',
              backgroundColor: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '8px',
              color: '#155724',
              marginBottom: '30px',
              textAlign: 'center'
            }}>
              {submitMessage}
            </div>
          )}

          {submitError && (
            <div style={{
              padding: '15px 20px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '8px',
              color: '#721c24',
              marginBottom: '30px',
              textAlign: 'center'
            }}>
              {submitError}
            </div>
          )}

          <div className="row justify-content-center">
            <div className="col-lg-10">
              {/* Introduction */}
              <div className="wow fadeInUp" style={{
                backgroundColor: 'white',
                padding: '40px',
                borderRadius: '15px',
                boxShadow: '0 5px 30px rgba(0, 0, 0, 0.08)',
                marginBottom: '30px',
                textAlign: 'center'
              }}>
                <h3 style={{ 
                  color: '#0C2239', 
                  fontSize: '32px', 
                  fontWeight: '700',
                  marginBottom: '20px'
                }}>
                  Your Donation Changes Lives
                </h3>
                <p style={{ 
                  color: '#666', 
                  fontSize: '16px', 
                  lineHeight: '1.8',
                  marginBottom: '20px'
                }}>
                  Every contribution, no matter the size, helps us provide care, education, and hope to children with special needs. 
                  Join us in making a lasting impact on their lives.
                </p>
                <div style={{
                  backgroundColor: '#ECFDF5',
                  padding: '25px',
                  borderRadius: '10px',
                  border: '2px solid #D1FAE5'
                }}>
                  <div className="row">
                    <div className="col-md-3 mb-3 mb-md-0">
                      <i className="fa-solid fa-heart" style={{ fontSize: '36px', color: '#03A84A', marginBottom: '10px' }}></i>
                      <h5 style={{ color: '#0C2239', fontSize: '18px', fontWeight: '600' }}>Healthcare</h5>
                      <p style={{ color: '#666', fontSize: '14px', marginBottom: '0' }}>Medical care & therapy</p>
                    </div>
                    <div className="col-md-3 mb-3 mb-md-0">
                      <i className="fa-solid fa-book" style={{ fontSize: '36px', color: '#03A84A', marginBottom: '10px' }}></i>
                      <h5 style={{ color: '#0C2239', fontSize: '18px', fontWeight: '600' }}>Education</h5>
                      <p style={{ color: '#666', fontSize: '14px', marginBottom: '0' }}>Learning & development</p>
                    </div>
                    <div className="col-md-3 mb-3 mb-md-0">
                      <i className="fa-solid fa-utensils" style={{ fontSize: '36px', color: '#03A84A', marginBottom: '10px' }}></i>
                      <h5 style={{ color: '#0C2239', fontSize: '18px', fontWeight: '600' }}>Nutrition</h5>
                      <p style={{ color: '#666', fontSize: '14px', marginBottom: '0' }}>Healthy meals daily</p>
                    </div>
                    <div className="col-md-3">
                      <i className="fa-solid fa-home" style={{ fontSize: '36px', color: '#03A84A', marginBottom: '10px' }}></i>
                      <h5 style={{ color: '#0C2239', fontSize: '18px', fontWeight: '600' }}>Shelter</h5>
                      <p style={{ color: '#666', fontSize: '14px', marginBottom: '0' }}>Safe living space</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Donation Form */}
              <form onSubmit={handleSubmit} noValidate>
                {/* Donation Type */}
                <div className="wow fadeInUp" data-wow-delay="100ms" style={{
                  backgroundColor: '#ECFDF5',
                  padding: '40px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 30px rgba(3, 168, 74, 0.1)',
                  marginBottom: '30px',
                  border: '2px solid #D1FAE5'
                }}>
                  <h4 style={{ 
                    color: '#0C2239', 
                    paddingBottom: '15px',
                    marginBottom: '25px',
                    fontSize: '22px',
                    fontWeight: '700',
                    borderBottom: '3px solid #03A84A',
                    display: 'inline-block'
                  }}>
                    <i className="fa-solid fa-hand-holding-heart me-2" style={{ color: '#03A84A' }}></i>
                    Donation Type <span style={{ color: '#03A84A' }}>*</span>
                  </h4>

                  <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    <label className="custom-radio" style={{
                      padding: '20px 30px',
                      backgroundColor: formData.donationType === 'one-time' ? 'white' : '#f9f9f9',
                      border: `2px solid ${formData.donationType === 'one-time' ? '#03A84A' : '#e8e8e8'}`,
                      borderRadius: '10px',
                      transition: 'all 0.3s ease',
                      flex: '1',
                      minWidth: '200px'
                    }}>
                      <input
                        type="radio"
                        name="donationType"
                        value="one-time"
                        checked={formData.donationType === 'one-time'}
                        onChange={() => handleRadioChange('donationType', 'one-time')}
                      />
                      <span className="radio-box"></span>
                      <div>
                        <strong style={{ color: '#0C2239', display: 'block', marginBottom: '5px', fontSize: '18px' }}>
                          One-Time Donation
                        </strong>
                        <span style={{ color: '#666', fontSize: '14px' }}>
                          Make a single contribution
                        </span>
                      </div>
                    </label>

                    <label className="custom-radio" style={{
                      padding: '20px 30px',
                      backgroundColor: formData.donationType === 'monthly' ? 'white' : '#f9f9f9',
                      border: `2px solid ${formData.donationType === 'monthly' ? '#03A84A' : '#e8e8e8'}`,
                      borderRadius: '10px',
                      transition: 'all 0.3s ease',
                      flex: '1',
                      minWidth: '200px'
                    }}>
                      <input
                        type="radio"
                        name="donationType"
                        value="monthly"
                        checked={formData.donationType === 'monthly'}
                        onChange={() => handleRadioChange('donationType', 'monthly')}
                      />
                      <span className="radio-box"></span>
                      <div>
                        <strong style={{ color: '#0C2239', display: 'block', marginBottom: '5px', fontSize: '18px' }}>
                          Monthly Donation 🌟
                        </strong>
                        <span style={{ color: '#666', fontSize: '14px' }}>
                          Become a sustaining donor
                        </span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Amount Selection */}
                <div className="wow fadeInUp" data-wow-delay="200ms" style={{
                  backgroundColor: '#ECFDF5',
                  padding: '40px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 30px rgba(3, 168, 74, 0.1)',
                  marginBottom: '30px',
                  border: '2px solid #D1FAE5'
                }}>
                  <h4 style={{ 
                    color: '#0C2239', 
                    paddingBottom: '15px',
                    marginBottom: '25px',
                    fontSize: '22px',
                    fontWeight: '700',
                    borderBottom: '3px solid #03A84A',
                    display: 'inline-block'
                  }}>
                    <i className="fa-solid fa-dollar-sign me-2" style={{ color: '#03A84A' }}></i>
                    Donation Amount (USD) <span style={{ color: '#03A84A' }}>*</span>
                  </h4>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                    {predefinedAmounts.map((amt) => (
                      <label key={amt} className="custom-radio" style={{
                        padding: '20px',
                        backgroundColor: formData.amount === amt ? 'white' : '#f9f9f9',
                        border: `2px solid ${formData.amount === amt ? '#03A84A' : '#e8e8e8'}`,
                        borderRadius: '10px',
                        transition: 'all 0.3s ease',
                        textAlign: 'center',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="radio"
                          name="amount"
                          value={amt}
                          checked={formData.amount === amt}
                          onChange={() => handleRadioChange('amount', amt)}

                          style={{ display: 'none' }}
                        />
                        <strong style={{ color: '#0C2239', fontSize: '24px', display: 'block' }}>
                          ${amt}
                        </strong>
                      </label>
                    ))}
                  </div>

                  <div>
                    <label className="custom-radio" style={{
                      padding: '15px 20px',
                      backgroundColor: formData.amount === 'custom' ? 'white' : '#f9f9f9',
                      border: `2px solid ${formData.amount === 'custom' ? '#03A84A' : '#e8e8e8'}`,
                      borderRadius: '10px',
                      transition: 'all 0.3s ease',
                      display: 'block',
                      marginBottom: '15px'
                    }}>
                      <input
                        type="radio"
                        name="amount"
                        value="custom"
                        checked={formData.amount === 'custom'}
                        onChange={() => handleRadioChange('amount', 'custom')}
                        style={{ display: 'none' }}
                      />
                      <strong style={{ color: '#0C2239', fontSize: '16px' }}>
                        Custom Amount
                      </strong>
                    </label>

                    {formData.amount === 'custom' && (
                      <input
                        type="number"
                        name="customAmount"
                        value={formData.customAmount}
                        onChange={handleInputChange}
                        required={formData.amount === 'custom'}
                        min="1"
                        placeholder="Enter custom amount in USD"
                        style={inputStyle}
                        onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                      />
                    )}
                  </div>
                </div>

                {/* Donor Information */}
                <div className="wow fadeInUp" data-wow-delay="300ms" style={{
                  backgroundColor: '#ECFDF5',
                  padding: '40px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 30px rgba(3, 168, 74, 0.1)',
                  marginBottom: '30px',
                  border: '2px solid #D1FAE5'
                }}>
                  <h4 style={{ 
                    color: '#0C2239', 
                    paddingBottom: '15px',
                    marginBottom: '25px',
                    fontSize: '22px',
                    fontWeight: '700',
                    borderBottom: '3px solid #03A84A',
                    display: 'inline-block'
                  }}>
                    <i className="fa-solid fa-user me-2" style={{ color: '#03A84A' }}></i>
                    Your Information
                  </h4>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Full Name <span style={{ color: '#03A84A' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                        style={inputStyle}
                        onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Email Address <span style={{ color: '#03A84A' }}>*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        style={inputStyle}
                        onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Contact Number
                      </label>
                      <input
                        type="tel"
                        name="contactNumber"
                        value={formData.contactNumber}
                        onChange={handleInputChange}
                        style={inputStyle}
                        onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Country <span style={{ color: '#03A84A' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        required
                        style={inputStyle}
                        onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Message (Optional)
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={4}
                        placeholder="Share why you want to support our children..."
                        style={{...inputStyle, resize: 'vertical'}}
                        onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Integration Notice */}
                <div className="wow fadeInUp" data-wow-delay="400ms" style={{
                  backgroundColor: '#ECFDF5',
                  padding: '40px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 30px rgba(3, 168, 74, 0.1)',
                  marginBottom: '30px',
                  border: '2px solid #D1FAE5'
                }}>
                  <h4 style={{ 
                    color: '#0C2239', 
                    paddingBottom: '15px',
                    marginBottom: '25px',
                    fontSize: '22px',
                    fontWeight: '700',
                    borderBottom: '3px solid #03A84A',
                    display: 'inline-block'
                  }}>
                    <i className="fa-solid fa-credit-card me-2" style={{ color: '#03A84A' }}></i>
                    Payment Integration Update
                  </h4>

                  <div style={{
                    marginTop: '8px',
                    padding: '15px',
                    backgroundColor: '#E8F4FD',
                    borderRadius: '8px',
                    border: '1px solid #B8DAFF'
                  }}>
                    <p style={{ color: '#004085', fontSize: '14px', marginBottom: '0', lineHeight: '1.6' }}>
                      <i className="fa-solid fa-info-circle me-2"></i>
                      <strong>Notice:</strong> We are integrating online payment options and they will be available soon. 
                      For now, submit this form and our team will contact you directly for donation completion.
                    </p>
                  </div>
                </div>

                {/* Terms & Submit */}
                <div className="wow fadeInUp" data-wow-delay="500ms" style={{
                  backgroundColor: 'white',
                  padding: '30px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 30px rgba(0, 0, 0, 0.08)',
                  marginBottom: '30px'
                }}>
                  <label className="custom-checkbox" style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    cursor: 'pointer',
                    padding: '15px',
                    backgroundColor: '#ECFDF5',
                    border: '2px solid #D1FAE5',
                    borderRadius: '10px'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.agreedToTerms}
                      onChange={handleCheckboxChange}
                      style={{ display: 'none' }}
                    />
                    <span className="checkbox-box" style={{ marginTop: '2px', flexShrink: 0 }}></span>
                    <span style={{ color: '#0C2239', lineHeight: '1.6' }}>
                      I confirm that I want to make this donation to New Life Nepal and understand that my donation 
                      will be used to support children with special needs. I agree to be contacted regarding my donation. <span style={{ color: '#03A84A' }}>*</span>
                    </span>
                  </label>

                  <button
                    type="submit"
                    style={{
                      width: '100%',
                      padding: '16px',
                      backgroundColor: '#03A84A',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '18px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      marginTop: '20px',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 5px 20px rgba(3, 168, 74, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#d63d15';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#03A84A';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <i className="fa-solid fa-heart me-2"></i>
                    {formData.donationType === 'monthly' ? 'Become a Monthly Donor' : 'Complete Donation'}
                  </button>
                </div>
              </form>

              {/* Contact Info */}
              <div className="wow fadeInUp" data-wow-delay="600ms" style={{
                backgroundColor: '#E8F4FD',
                padding: '30px',
                borderRadius: '15px',
                textAlign: 'center'
              }}>
                <h5 style={{ color: '#0C2239', fontWeight: '700', marginBottom: '15px' }}>
                  Questions About Donating?
                </h5>
                <p style={{ color: '#666', marginBottom: '15px' }}>
                  <i className="fa-solid fa-phone me-2" style={{ color: '#03A84A' }}></i>
                  <a href="tel:+9779851031573" style={{ color: '#0C2239', textDecoration: 'none' }}>
                    +977 9851031573
                  </a>
                </p>
                <p style={{ color: '#666', marginBottom: '15px' }}>
                  <i className="fa-solid fa-envelope me-2" style={{ color: '#03A84A' }}></i>
                  <a href="mailto:info@newlifenepal.org" style={{ color: '#0C2239', textDecoration: 'none' }}>
                    info@newlifenepal.org
                  </a>
                </p>
                <p style={{ color: '#666', marginBottom: '0' }}>
                  <i className="fa-solid fa-location-dot me-2" style={{ color: '#03A84A' }}></i>
                  Gokarneshwor-5, Kathmandu, Nepal
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Donate;
